import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useTheme } from "react-jss";
import { IGenericPayload } from "../../../../../../../models";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux/hooks";
import { compileNameOfProperty, getGenericPayloadFromEvent, mapIDescriptionToDropDownOptions } from "../../../../../../../utils";
import { MasterCustomsStatesFormStyle } from "./formStyle.jss";
import React, { useCallback, useMemo } from "react";
import { Selector } from "../../../../../../common/selectors/selector";
import { NotDataWarningMessage } from "../../../../notDataWarning/notDataWarning";
import { DefaultValues } from "../../../../../../../common/enum/DefaultValues";
import { FormField } from "../../../../../../common/fields/fields";
import { IDropdownOption } from "@fluentui/react";
import { BooleanSelector } from "../../../../../../request/common/selectors";
import { PopUpCodes } from "../../../../../../../common/enum/PopUpCodes";
import { IMasterCustomsStateRemaining, IMasterCustomsState } from "../../../../../../../models/resources/master/ICustomsState";
import { updateMasterCustomsStatesProperty, setMasterCustomsStatesForm } from "../../../../../../../redux/reducers/resources/master/customsStates/form";

interface IMasterCustomsStatesFormProps {
    showNotData: boolean;
    selectorData: IMasterCustomsStateRemaining[];
}

export const MasterCustomsStatesForm: React.FC<IMasterCustomsStatesFormProps> = ({ selectorData, showNotData }) => {
    const theme = useTheme();
    const style = MasterCustomsStatesFormStyle({ theme });
    const dispatch = useAppDispatch();
    const { t } = useTranslation("common", { keyPrefix: "MASTERS" });
    const { form } = useAppSelector((state) => state.masterCustomsStatesForm);
    const { showPopUp } = useAppSelector((state) => state.masterPopUp);
    const { register, control } = useForm<IMasterCustomsState>({ mode: "onChange" });

    const onUpdateFormProperty = (name: string, value: any) => {
        const payload: IGenericPayload = {
            name,
            value,
        };
        dispatch(updateMasterCustomsStatesProperty(payload));
    };

    const onInputChange = useCallback(
        (event: any) => {
            const { name, value } = getGenericPayloadFromEvent(event);
            onUpdateFormProperty(name, value);
        },
        [dispatch],
    );

    const onSelectorChange = useCallback(
        (event: any, option: any, name: string) => {
            onUpdateFormProperty(name, String(option.key));

            if (showPopUp === PopUpCodes.CREATE) {
                let customState = selectorData.find((item) => item.id === option.key);
                if (customState) {
                    let newForm: IMasterCustomsState = {
                        ...form,
                        referenceId: String(customState.id),
                        active: true,
                        code: customState.code,
                        description: customState.description,
                        flow: customState.flow,
                        temporaryRegime: false,
                        type: customState.type ?? "",
                    };
                    dispatch(setMasterCustomsStatesForm(newForm));
                }
            }
        },
        [dispatch, selectorData],
    );

    const onYesNoOptionsChange = (option: IDropdownOption, propertyName: string) => {
        onUpdateFormProperty(propertyName, !!+option.key);
    };

    const disabledInput = useMemo(() => {
        return showPopUp === PopUpCodes.CREATE && !form.referenceId;
    }, [showPopUp, form]);

    return showNotData ? (
        <NotDataWarningMessage></NotDataWarningMessage>
    ) : (
        <div className={style.form}>
            {!form.id && (
                <div className={style.rowForm}>
                    <Selector
                        selectedKey={Number(form.referenceId)}
                        name={compileNameOfProperty<IMasterCustomsState>("referenceId")}
                        title={t("FORM.CUSTOMS_STATES")}
                        options={mapIDescriptionToDropDownOptions(selectorData)}
                        onChange={onSelectorChange}
                        required={true}
                    />
                </div>
            )}
            <div className={style.rowForm}>
                <FormField
                    type="text"
                    label={t("CODE")}
                    value={form.code ?? DefaultValues.NOT_TEXT}
                    {...register(compileNameOfProperty<IMasterCustomsState>("code"), {
                        onChange: onInputChange,
                        value: form.code ?? DefaultValues.NOT_TEXT,
                    })}
                    isRequired={true}
                    disabled={disabledInput}
                />

                <FormField
                    type="text"
                    label={t("DESCRIPTION")}
                    value={form.description ?? DefaultValues.NOT_TEXT}
                    {...register(compileNameOfProperty<IMasterCustomsState>("description"), {
                        onChange: onInputChange,
                        value: form.description ?? DefaultValues.NOT_TEXT,
                    })}
                    isRequired={true}
                    disabled={disabledInput}
                />
            </div>

            <div className={style.rowForm}>
                <FormField
                    type="text"
                    label={t("TYPE")}
                    value={form.type ?? DefaultValues.NOT_TEXT}
                    {...register(compileNameOfProperty<IMasterCustomsState>("type"), {
                        onChange: onInputChange,
                        value: form.type ?? DefaultValues.NOT_TEXT,
                    })}
                    isRequired={false}
                    disabled={true}
                />

                <FormField
                    type="text"
                    label={t("FLOW")}
                    value={form.flow ?? DefaultValues.NOT_TEXT}
                    {...register(compileNameOfProperty<IMasterCustomsState>("flow"), {
                        onChange: onInputChange,
                        value: form.flow ?? DefaultValues.NOT_TEXT,
                    })}
                    isRequired={false}
                    disabled={true}
                />
            </div>

            <div className={style.rowForm}>
                <BooleanSelector
                    onChange={onYesNoOptionsChange}
                    selectedKey={+form.temporaryRegime}
                    propertyName={compileNameOfProperty<IMasterCustomsState>("temporaryRegime")}
                    title={t("TEMPORAL_REGIME")}
                    control={control}
                    isRequired={true}
                    disabled={disabledInput}
                />
                <BooleanSelector
                    onChange={onYesNoOptionsChange}
                    selectedKey={+form.active}
                    propertyName={compileNameOfProperty<IMasterCustomsState>("active")}
                    title={t("ACTIVE")}
                    control={control}
                    isRequired={true}
                    disabled={disabledInput}
                />
            </div>
        </div>
    );
};
